import React, { useState, useEffect } from 'react';
import { Plus, Search, HelpCircle, Loader2, Filter, Grid3X3, List, FileText, ChevronDown, TrendingUp, AlertTriangle, Crown, Trash2, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import PostList from '../components/posts/PostList';
import postService from '../services/postService';
import { useDebounce } from '../hooks/use-debounce';
import { useTour } from '../contexts/TourContext';
import { postsSteps } from '../components/onboarding/tour-steps';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import ComingSoonWrapper from '../components/launch/ComingSoonWrapper';
import { useMonthlyPostsLimit } from '../hooks/useSocialMediaLimits';
import LimitWarning from '../components/limits/LimitWarning';
import PostGroupHelpWidget from '../components/posts/PostGroupHelpWidget';
import { UpgradeModal } from '../components/modals/UpgradeModal';
import { useClient } from '../contexts/ClientContext';
import { useToast } from '../hooks/use-toast';
import { PostGroup } from '../services/postService';

interface PostsPageProps {}

const PostsPage: React.FC<PostsPageProps> = () => {
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(24);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const navigate = useNavigate();
  const { startTour, restartTour } = useTour();
  const { currentClient, canDelete } = useClient();
  const { toast } = useToast();
  
  // Get monthly posts limit info from user's tier (not client)
  const monthlyLimit = useMonthlyPostsLimit();
  
  // Check if feature is locked (limit is 0 from tier)
  // The limit comes from the user's tier, not the client
  const isPostFeatureLocked = !monthlyLimit.isLoading && (
    monthlyLimit.limit === 0 || 
    (monthlyLimit.unlimited === false && monthlyLimit.limit === 0)
  );
  
  // Debug logging
  useEffect(() => {
    console.log('Monthly limit data:', {
      limit: monthlyLimit.limit,
      unlimited: monthlyLimit.unlimited,
      current_count: monthlyLimit.current_count,
      allowed: monthlyLimit.canPublish,
      isLoading: monthlyLimit.isLoading
    });
    console.log('Is post feature locked:', isPostFeatureLocked);
  }, [monthlyLimit, isPostFeatureLocked]);

  useEffect(() => {
    startTour(postsSteps, 'posts');
  }, [startTour]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['post-groups', selectedStatus, debouncedSearchTerm, currentPage],
    queryFn: () => postService.getPostGroups(
      currentPage,
      pageSize,
      selectedStatus === 'all' ? undefined : selectedStatus,
      debouncedSearchTerm || undefined
    ),
    refetchInterval: 5000,
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedPosts(new Set()); // Clear selections when filters change
  }, [selectedStatus, debouncedSearchTerm]);

  // Clear selections when page changes
  useEffect(() => {
    setSelectedPosts(new Set());
  }, [currentPage]);

  const postGroups = data?.post_groups || [];

  const handlePostSelect = (postId: string) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(postId)) {
      newSelected.delete(postId);
    } else {
      newSelected.add(postId);
    }
    setSelectedPosts(newSelected);
  };

  const selectAll = () => {
    const currentPagePostIds = new Set(postGroups.map(post => post.id));
    setSelectedPosts(currentPagePostIds);
  };

  const deselectAll = () => {
    setSelectedPosts(new Set());
  };

  const handleBulkDelete = async () => {
    if (!currentClient || selectedPosts.size === 0) return;

    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedPosts).map(postId => 
        postService.deletePostGroup(postId)
      );
      
      await Promise.all(deletePromises);
      
      setSelectedPosts(new Set());
      toast({
        title: "Posts deleted successfully",
        description: `${selectedPosts.size} post${selectedPosts.size > 1 ? 's' : ''} deleted.`
      });
      
      refetch(); // Refresh the posts list
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast({
        title: "Delete failed",
        description: "Some posts could not be deleted. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  return (
    <ComingSoonWrapper
        feature="publishing"
        title="Upgrade to Unlock Publishing"
        description="Connect and publish directly to your social media accounts with a higher tier plan."
        features={[
          "Direct publishing to all platforms",
          "Automatic caption & hashtag generation",
          "Cross-platform optimization",
          "Real-time engagement tracking"
        ]}

      >
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white relative">
      {/* Page Header */}
      <div className="backdrop-blur-2xl bg-white/60 dark:bg-slate-900/60 border-b border-slate-200/50 dark:border-slate-700/30">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-grow"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <div className="flex items-center space-x-3">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black bg-gradient-to-r from-gray-900 via-indigo-500 to-purple-500 bg-clip-text text-transparent dark:from-white dark:via-indigo-200 dark:to-purple-200">
                      Posts
                    </h1>
                    {/* Monthly Usage Badge - Only show if limit > 0 */}
                    {!monthlyLimit.unlimited && !isPostFeatureLocked && monthlyLimit.limit > 0 && (
                      <div className={`hidden sm:flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-medium ${
                        !monthlyLimit.canPublish 
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                          : (monthlyLimit.current_count || 0) >= (monthlyLimit.limit || 0) * 0.8
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        <TrendingUp className="w-3 h-3" />
                        <span>{monthlyLimit.current_count || 0}/{monthlyLimit.limit || 0} content</span>
                        {!monthlyLimit.canPublish && <AlertTriangle className="w-3 h-3" />}
                      </div>
                    )}
                  </div>
                  <p className="hidden sm:block text-slate-600 dark:text-slate-300 text-sm lg:text-lg">Craft amazing social content</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="flex items-center space-x-2 sm:space-x-4"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center bg-white/50 dark:bg-slate-800/30 backdrop-blur-sm rounded-xl border border-slate-300/50 dark:border-slate-600/30 p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={`
                    ${viewMode === 'grid' 
                      ? 'bg-indigo-500/10 text-indigo-600 shadow-sm dark:bg-indigo-500/30 dark:text-indigo-300' 
                      : 'text-slate-500 dark:text-slate-400'}
                    hover:bg-indigo-500/20 dark:hover:bg-indigo-500/40 transition-all duration-200
                  `}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={`
                    ${viewMode === 'list' 
                      ? 'bg-indigo-500/10 text-indigo-600 shadow-sm dark:bg-indigo-500/30 dark:text-indigo-300' 
                      : 'text-slate-500 dark:text-slate-400'}
                    hover:bg-indigo-500/20 dark:hover:bg-indigo-500/40 transition-all duration-200
                  `}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              
              <Button
                id="create-post-button"
                onClick={() => isPostFeatureLocked ? setShowUpgradeModal(true) : navigate('/app/posts/new')}
                className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 transform hover:scale-105 text-sm sm:text-base"
              >
                {isPostFeatureLocked && <Crown className="h-4 w-4 mr-2 text-yellow-300" />}
                <span>New Post</span>
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Floating Help Widget */}
      <PostGroupHelpWidget />

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Limit Warning - Only show if feature is not locked (limit > 0) */}
        {!warningDismissed && !isPostFeatureLocked && (
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <LimitWarning onDismiss={() => setWarningDismissed(true)} />
          </motion.div>
        )}

        <motion.div 
          className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="flex-1 w-full relative">
            <Search className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search your posts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 sm:pl-14 pr-6 py-3 sm:py-4 bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30 rounded-2xl text-gray-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all duration-200"
            />
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className={`w-full sm:w-auto px-6 py-3 sm:py-4 rounded-2xl backdrop-blur-sm transition-all duration-200
                  ${selectedStatus !== 'all' 
                    ? 'bg-indigo-500/10 border-indigo-500/50 text-indigo-600 dark:bg-indigo-500/20 dark:border-indigo-500/50 dark:text-indigo-300' 
                    : 'text-slate-600 border-slate-300/50 bg-white/70 hover:bg-slate-100/50 dark:text-slate-300 dark:border-slate-600/30 dark:bg-slate-800/40'}
                `}
              >
                <Filter className="h-5 w-5 mr-2" />
                {selectedStatus === 'all' ? 'All Statuses' : 
                 selectedStatus === 'draft' ? 'Draft' :
                 selectedStatus === 'scheduled' ? 'Scheduled' : 'Published'}
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-48">
              <DropdownMenuItem onClick={() => setSelectedStatus('all')}>
                All Statuses
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedStatus('draft')}>
                Draft
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedStatus('scheduled')}>
                Scheduled
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedStatus('published')}>
                Published
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </motion.div>

        {/* Selection Controls */}
        {postGroups.length > 0 && (
          <motion.div 
            className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-white/70 dark:bg-slate-800/40 backdrop-blur-sm border border-slate-300/50 dark:border-slate-600/30 rounded-xl px-3 py-2">
                <Button
                  onClick={selectAll}
                  size="sm"
                  variant="ghost"
                  className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  Select All on Page
                </Button>
                {selectedPosts.size > 0 && (
                  <>
                    <div className="w-px h-4 bg-slate-300 dark:bg-slate-600"></div>
                    <Button
                      onClick={deselectAll}
                      size="sm"
                      variant="ghost"
                      className="text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                    >
                      Deselect All
                    </Button>
                  </>
                )}
              </div>

              {selectedPosts.size > 0 && (
                <div className="flex items-center space-x-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 px-4 py-2 rounded-xl border border-blue-200/50 dark:border-blue-600/30">
                  <span className="text-blue-700 dark:text-blue-300 font-medium">
                    {selectedPosts.size} selected
                  </span>
                  {canDelete() && (
                    <Button
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      size="sm"
                      variant="destructive"
                      className="bg-red-500 hover:bg-red-600 text-white"
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {isDeleting ? 'Deleting...' : `Delete (${selectedPosts.size})`}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}


        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="w-20 h-20 border-4 border-slate-200/50 dark:border-slate-700/30 border-t-indigo-500 rounded-full animate-spin mb-8"></div>
              <h3 className="text-2xl font-semibold text-slate-700 dark:text-slate-200">Loading your posts</h3>
              <p className="text-slate-500 dark:text-slate-400 text-lg">Organizing your creative content...</p>
            </div>
          ) : isError ? (
            <div className="text-center py-32">
              <div className="w-24 h-24 mx-auto bg-red-500/10 backdrop-blur-sm rounded-full flex items-center justify-center mb-8 border border-red-200/50 shadow-lg shadow-red-100/50 dark:border-red-500/20">
                <HelpCircle className="h-12 w-12 text-red-500" />
              </div>
              <h3 className="text-2xl font-semibold text-red-500 dark:text-red-400 mb-3">Something went wrong</h3>
              <p className="text-slate-600 dark:text-slate-400 mb-8 text-lg">We couldn't load your posts right now.</p>
              <Button 
                onClick={refetch} 
                className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-8 py-3 rounded-xl shadow-lg hover:shadow-red-500/25 transition-all duration-300 transform hover:scale-105"
              >
                Try again
              </Button>
            </div>
          ) : postGroups.length > 0 ? (
            <>
              <PostList 
                postGroups={postGroups} 
                onDeleteSuccess={refetch} 
                viewMode={viewMode}
                selectedPosts={selectedPosts}
                onPostSelect={handlePostSelect}
              />
              
              {/* Pagination Controls */}
              {data && data.total > pageSize && (
                <div className="flex items-center justify-between mt-8 px-4 sm:px-6">
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, data.total)} of {data.total} post groups
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={!data.has_prev}
                      variant="outline"
                      size="sm"
                      className="px-3 py-2"
                    >
                      Previous
                    </Button>
                    
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const totalPages = Math.ceil(data.total / pageSize);
                        const maxPagesToShow = 5;
                        const pages = [];
                        
                        if (totalPages <= maxPagesToShow) {
                          // Show all pages if total is small
                          for (let i = 1; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          // Show smart pagination
                          if (currentPage <= 3) {
                            pages.push(1, 2, 3, 4, 5);
                          } else if (currentPage >= totalPages - 2) {
                            pages.push(totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
                          } else {
                            pages.push(currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2);
                          }
                        }
                        
                        return pages.map(pageNum => (
                          <Button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            variant={pageNum === currentPage ? "default" : "outline"}
                            size="sm"
                            className={`w-8 h-8 p-0 ${pageNum === currentPage ? 'bg-primary text-primary-foreground' : ''}`}
                          >
                            {pageNum}
                          </Button>
                        ));
                      })()}
                    </div>
                    
                    <Button
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!data.has_next}
                      variant="outline"
                      size="sm"
                      className="px-3 py-2"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 sm:py-12">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <FileText className="w-6 h-6 sm:w-7 sm:h-7 text-indigo-500 dark:text-indigo-400" />
              </div>
              
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-2">
                {searchTerm || selectedStatus !== 'all' ? 'No matching posts' : 'Ready to create?'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-6 max-w-md mx-auto px-4">
                {searchTerm || selectedStatus !== 'all' 
                  ? 'No posts match your current filters. Try adjusting your search criteria.'
                  : 'Start creating engaging social media content for your audience.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-sm sm:max-w-none mx-auto">
                {(searchTerm || selectedStatus !== 'all') && (
                  <Button 
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedStatus('all');
                    }}
                    variant="outline"
                    className="border-slate-300/50 bg-white/70 text-slate-600 hover:bg-slate-100/50 backdrop-blur-sm shadow-lg shadow-slate-200/50 dark:border-slate-600/50 dark:bg-slate-800/40 dark:text-slate-300 px-6 py-2.5 rounded-xl transition-all duration-200"
                  >
                    Clear Filters
                  </Button>
                )}
                <Button 
                  onClick={() => isPostFeatureLocked ? setShowUpgradeModal(true) : navigate('/app/posts/new')}
                  className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 text-white px-6 py-2.5 rounded-xl font-semibold shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 transform hover:scale-105"
                >
                  {isPostFeatureLocked && (
                    <Crown className="h-4 w-4 mr-2 text-yellow-300" />
                  )}
                  <span className="hidden sm:inline">{isPostFeatureLocked ? 'Upgrade to Create Posts' : 'Create Your First Post'}</span>
                  <span className="sm:hidden">Create Post</span>
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
      </div>
      
      {/* Upgrade Modal for locked features */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        title="Upgrade to Create Posts"
        message="Creating and publishing posts is available on paid plans. Upgrade now to unlock this feature and start sharing your content."
        feature="posts"
      />

      {/* Bulk Delete Confirmation Dialog */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white dark:bg-slate-800/95 backdrop-blur-xl border border-slate-200/50 dark:border-slate-600/30 rounded-2xl p-6 w-full max-w-md"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 dark:text-slate-200">
                Delete Selected Posts
              </h3>
            </div>
            
            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                Are you sure you want to delete <span className="font-semibold text-slate-800 dark:text-slate-200">{selectedPosts.size}</span> selected post{selectedPosts.size > 1 ? 's' : ''}?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <Button
                onClick={() => setShowBulkDeleteConfirm(false)}
                variant="outline"
                className="flex-1 bg-slate-100/50 hover:bg-slate-200/50 dark:bg-slate-700/50 border-slate-300/70 dark:border-slate-600/50 dark:hover:bg-slate-600/50 text-slate-700 dark:text-slate-300"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkDelete}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-lg hover:shadow-red-500/25"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  `Delete ${selectedPosts.size} Post${selectedPosts.size > 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </ComingSoonWrapper>
  );
};

export default PostsPage;